
import { useState } from 'react';
import { Button, Stack, Text } from '@client/shared/design';
import { useForm, zodValidator, TextField } from '@client/shared/form/index.ts';
import { AuthGate, ForgotPasswordInputSchema, authPost } from '@client/shared/auth/index.ts';

async function postPasswordReset(email: string): Promise<boolean> {
  try {
    const response = await authPost('/auth/password-reset', 'password-reset', { email });
    return response.ok;
  } catch {
    return false;
  }
}

export function ForgotPassword() {
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const form = useForm({
    defaultValues: { email: '' },
    validators: { onSubmit: zodValidator(ForgotPasswordInputSchema) },
    onSubmit: async ({ value }) => {
      setFormError(null);
      const email = value.email.trim();
      const ok = await postPasswordReset(email);
      if (!ok) {
        setFormError('Couldn’t start the reset: please try again.');
        return;
      }
      setSentTo(email);
    },
  });

  return (
    <AuthGate
      eyebrow="Account recovery"
      heading="Forgot your password?"
      intro="Enter your account email and we’ll send a link to choose a new password."
    >
      {sentTo ? (
        <Stack gap={4}>
          <Text size="sm" tone="accent" role="status">
            Check your inbox at {sentTo}: if an account exists, a reset link is on its way.
          </Text>
          <Text size="sm" tone="soft">
            <a href="/login">Back to sign in</a>
          </Text>
        </Stack>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void form.handleSubmit();
          }}
          noValidate
        >
          <Stack gap={4}>
            <form.Field name="email">
              {(field) => (
                <TextField
                  field={field}
                  label="Email"
                  type="email"
                  autoComplete="email"
                  inputMode="email"
                  placeholder="you@yourgroup.com"
                />
              )}
            </form.Field>

            {formError && (
              <Text size="sm" tone="danger" role="alert">
                {formError}
              </Text>
            )}

            <form.Subscribe selector={(s) => s.isSubmitting}>
              {(isSubmitting) => (
                <Button type="submit" size="lg" block loading={isSubmitting}>
                  Email me a reset link
                </Button>
              )}
            </form.Subscribe>

            <Text size="sm" tone="soft">
              <a href="/login">Back to sign in</a>
            </Text>
          </Stack>
        </form>
      )}
    </AuthGate>
  );
}
